import React, { useState, useEffect } from 'react';
import { FiPlus, FiEdit2, FiTrash2, FiCheck, FiX, FiStar } from 'react-icons/fi';
import { useTranslation } from 'react-i18next';
import { noteService } from '../services/noteService';
import { Note, NoteFormData } from '../types';
import './Notes.css';

const Notes: React.FC = () => {
  const { t } = useTranslation();
  const [notes, setNotes] = useState<Note[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [showModal, setShowModal] = useState<boolean>(false);
  const [editingNote, setEditingNote] = useState<Note | null>(null);
  const [formData, setFormData] = useState<NoteFormData>({
    title: '',
    content: '',
    tags: []
  });

  useEffect(() => {
    fetchNotes();
  }, []);

  const fetchNotes = async (): Promise<void> => {
    try {
      const notes = await noteService.getNotes();
      setNotes(notes);
      setLoading(false);
    } catch (error) {
      console.error('Notlar yüklenemedi:', error);
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    
    try {
      if (editingNote) {
        await noteService.updateNote(editingNote._id, formData);
      } else {
        await noteService.createNote(formData);
      }
      
      fetchNotes();
      closeModal();
    } catch (error) {
      console.error('Not kaydedilemedi:', error);
    }
  };

  const handleDelete = async (id: string): Promise<void> => {
    if (window.confirm(t('notes.deleteConfirm'))) {
      try {
        await noteService.deleteNote(id);
        fetchNotes();
      } catch (error) {
        console.error('Not silinemedi:', error);
      }
    }
  };

  const handlePin = async (id: string): Promise<void> => {
    const noteToPin = notes.find(note => note._id === id);
    if (!noteToPin) return;

    try {
      await noteService.toggleNotePin(id, !noteToPin.isPinned);
      fetchNotes();
    } catch (error) {
      console.error('Not sabitlenemedi:', error);
    }
  };

  const openModal = (note: Note | null = null): void => {
    if (note) {
      setEditingNote(note);
      setFormData({
        title: note.title,
        content: note.content,
        tags: note.tags || []
      });
    } else {
      setEditingNote(null);
      setFormData({
        title: '',
        content: '',
        tags: []
      });
    }
    setShowModal(true);
  };

  const closeModal = (): void => {
    setShowModal(false);
    setEditingNote(null);
  };

  if (loading) {
    return (
      <div className="page-loading">
        <div className="spinner"></div>
        <p>Yükleniyor...</p>
      </div>
    );
  }

  return (
    <div className="notes-page fade-in">
      <div className="page-header">
        <div>
          <h1 className="page-title">{t('notes.title')}</h1>
          <p className="page-subtitle">{t('notes.subtitle')}</p>
        </div>
        <button className="btn btn-primary" onClick={() => openModal()}>
          <FiPlus /> {t('notes.addNote')}
        </button>
      </div>

      {notes.length === 0 ? (
        <div className="empty-state">
          <div className="empty-state-icon">📝</div>
          <h3 className="empty-state-title">{t('notes.noNotes')}</h3>
          <p className="empty-state-text">{t('notes.createFirst')}</p>
          <button className="btn btn-primary" onClick={() => openModal()}>
            <FiPlus /> {t('notes.addNote')}
          </button>
        </div>
      ) : (
        <div className="notes-grid">
          {notes.map(note => (
            <div 
              key={note._id} 
              className="note-card"
            >
              <div className="note-card-header">
                <button
                  className={`btn-pin ${note.isPinned ? 'pinned' : ''}`}
                  onClick={() => handlePin(note._id)}
                  title={note.isPinned ? t('notes.actions.unpin') : t('notes.actions.pin')}
                >
                  <FiStar />
                </button>
              </div>
              
              <h3 className="note-card-title">{note.title}</h3>
              <p className="note-card-content">{note.content}</p>
              
              {note.tags && note.tags.length > 0 && (
                <div className="note-tags">
                  {note.tags.map((tag: string, index: number) => (
                    <span key={index} className="note-tag">#{tag}</span>
                  ))}
                </div>
              )}
              
              <div className="note-card-actions">
                <button 
                  className="btn-icon btn-edit" 
                  onClick={() => openModal(note)}
                  title={t('common.edit')}
                >
                  <FiEdit2 />
                </button>
                <button 
                  className="btn-icon btn-delete" 
                  onClick={() => handleDelete(note._id)}
                  title={t('common.delete')}
                >
                  <FiTrash2 />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {showModal && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editingNote ? t('notes.editNote') : t('notes.newNote')}</h2>
              <button className="modal-close" onClick={closeModal}>
                <FiX />
              </button>
            </div>
            
            <form onSubmit={handleSubmit} className="modal-form">
              <div className="input-group">
                <label className="input-label">Başlık *</label>
                <input
                  type="text"
                  className="input-field"
                  value={formData.title}
                  onChange={(e) => setFormData({...formData, title: e.target.value})}
                  required
                />
              </div>

              <div className="input-group">
                <label className="input-label">İçerik *</label>
                <textarea
                  className="textarea-field"
                  value={formData.content}
                  onChange={(e) => setFormData({...formData, content: e.target.value})}
                  rows={6}
                  required
                />
              </div>

              <div className="modal-actions">
                <button type="button" className="btn btn-outline" onClick={closeModal}>
                  {t('common.cancel')}
                </button>
                <button type="submit" className="btn btn-primary">
                  <FiCheck /> {editingNote ? t('common.update') : t('common.create')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Notes;

